/**
 * useBuilderHistory — Wraps useUndoRedo with debounced vs. immediate push
 * semantics and syncs state back to the caller via an `onRestore` callback.
 *
 * Property changes (e.g. font-size slider) use `pushDebounced` — only the
 * final value within a 500 ms window is committed to history.
 *
 * Structural changes (add/delete/reorder section) use `pushImmediate` — the
 * snapshot is committed right away and any pending debounce is cancelled.
 */

import { useCallback, useRef, useEffect } from 'react';
import useUndoRedo from './useUndoRedo';

const DEBOUNCE_MS = 500;

export default function useBuilderHistory({ onRestore } = {}) {
  const {
    state: historyState,
    pushState: pushToHistory,
    undo,
    redo,
    reset,
    canUndo,
    canRedo,
  } = useUndoRedo(null);

  const debounceRef = useRef(null);

  // Debounced push — collapses rapid property updates into one history entry
  const pushDebounced = useCallback(
    (newSections) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        pushToHistory(newSections);
        debounceRef.current = null;
      }, DEBOUNCE_MS);
    },
    [pushToHistory],
  );

  // Immediate push — for structural changes; cancels any pending debounce
  const pushImmediate = useCallback(
    (newSections) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      pushToHistory(newSections);
    },
    [pushToHistory],
  );

  // Clear pending debounce on unmount to prevent state updates after teardown
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  // Sync restored state back to the consumer (e.g. setSections)
  useEffect(() => {
    if (historyState !== null && onRestore) {
      onRestore(historyState);
    }
  }, [historyState, onRestore]);

  return {
    /** The current history snapshot (null initially). Changes on undo/redo. */
    historyState,
    canUndo,
    canRedo,
    undo,
    redo,
    reset,
    pushDebounced,
    pushImmediate,
  };
}
